'use client';

import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { createBrowserClient } from '@/lib/supabase-clients';
import { Session, User } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { logPhoneFormat, isValidE164 } from '@/lib/utils/phone-validation';
import { createLogger } from '@/lib/utils/logger';
import { raceWithTimeout } from '@/lib/utils/promise-timeout';

// debug/info are suppressed in production (WARN+ only) and escalate with AUTH_DEBUG.
const logger = createLogger('auth:SupabaseAuthContext');

// Defensive upper bound on the initial getSession() during auth init. The auth
// lock is already bounded (lib/supabase-clients.ts), so this only guards against
// a pathologically slow network — kept below the AuthLoader fallback window so a
// slow init resolves (or recovers) before the "taking too long" screen appears.
const INIT_GETSESSION_TIMEOUT_MS = 10000;

// Simple interface for user profile
export interface UserProfile {
  id: string;
  auth_id: string;
  name: string | null;
  email: string | null;
  phone?: string | null;
  phone_verified?: boolean;
  handle: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
  domain_id?: string | null;
  active_domain_id?: string | null; // CRITICAL: User's currently active domain
  active_domain_handle?: string | null;
  active_domain_role?: string | null;
}

// Authentication status
type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

// Auth context interface
interface SupabaseAuthContextType {
  // Auth state
  isAuthenticated: boolean;
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  status: AuthStatus;
  error: string | null;
  isLoading: boolean;
  
  // Auth functions
  signInWithEmail: (email: string, captchaToken?: string) => Promise<{ success: boolean; error: string | null; needsPhone?: boolean; maskedPhone?: string; fullPhone?: string }>;
  signInWithPhone: (phone: string, email?: string, captchaToken?: string) => Promise<{ success: boolean; error: string | null; fallbackUsed?: boolean }>;
  signInWithOAuth: (provider: 'azure') => Promise<{ success: boolean; error: string | null }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<Session | null>;
  
  // Profile functions
  refreshProfile: () => Promise<UserProfile | null>;
  
  // Backward compatibility
  isUsingImprovedAuth?: boolean;
  authStatus?: AuthStatus;
}

// Create the context
const SupabaseAuthContext = createContext<SupabaseAuthContextType | undefined>(undefined);

// Auth provider component
export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  // Use the singleton Supabase client
  const supabase = createBrowserClient();
  const router = useRouter();
  
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initTimeout, setInitTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastRefreshTime = useRef<number>(0);
  
  // Compute authentication status
  const isAuthenticated = !!user && status === 'authenticated';
  
  // Profile persistence ref
  const profileRef = useRef<UserProfile | null>(null);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // Bumped on SIGNED_OUT. Deferred auth work (see the onAuthStateChange
  // DEADLOCK GUARD) captures the epoch at schedule time and stands down if a
  // sign-out landed in between — never resurrect a cleared profile.
  const authEpochRef = useRef(0);
  
  // In-flight profile fetch, shared across concurrent callers. Auth init and the
  // INITIAL_SESSION callback both request the profile at page load — without this
  // they issue two identical queries (and two 5s timers) for the same user.
  const inFlightProfileFetch = useRef<{
    userId: string;
    promise: Promise<UserProfile | null>;
  } | null>(null);

  // Fetch user profile with timeout
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    const inFlight = inFlightProfileFetch.current;
    if (inFlight && inFlight.userId === userId) {
      logger.debug('fetchProfile joined in-flight request', { userId });
      return inFlight.promise;
    }

    logger.debug('fetchProfile called', { userId });

    // Create the fetch promise
    const fetchPromise = (async () => {
      try {
        logger.debug('Starting profile query...');
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('auth_id', userId)
          .maybeSingle(); // Use maybeSingle() instead of single() to handle 0 or 1 rows gracefully

        logger.debug('Profile query result', { data: !!data, error: !!error, errorMessage: error?.message });

        if (error) {
          logger.error('Error fetching user profile', error);
          return null;
        }

        if (!data) {
          logger.warn('No profile found for user', { userId });
          return null;
        }

        logger.debug('Profile found', { id: data.id, handle: data.handle });
        const userProfile = data as UserProfile;

        // Enrich with active domain handle and role for team context
        if (userProfile.active_domain_id && userProfile.active_domain_id !== userProfile.domain_id) {
          try {
            const [domainResult, memberResult] = await Promise.all([
              supabase.from('domains').select('handle').eq('id', userProfile.active_domain_id).maybeSingle(),
              supabase.from('domain_members').select('role').eq('domain_id', userProfile.active_domain_id).eq('user_id', userProfile.id).maybeSingle(),
            ]);
            userProfile.active_domain_handle = domainResult.data?.handle || null;
            userProfile.active_domain_role = memberResult.data?.role || null;
          } catch {
            // Non-blocking — fall back to personal room
          }
        }

        return userProfile;
      } catch (error) {
        logger.error('Unexpected error in fetchProfile', error);
        return null;
      }
    })();

    // Race between fetch and timeout. raceWithTimeout cancels its timer once the
    // race settles, so the warning fires only on a GENUINE timeout — never as a
    // 5s-later phantom for a fetch that already succeeded.
    const entry: { userId: string; promise: Promise<UserProfile | null> } = {
      userId,
      promise: Promise.resolve(null),
    };
    entry.promise = (async () => {
      try {
        return await raceWithTimeout(fetchPromise, 5000, () =>
          logger.warn('Profile fetch timed out after 5 seconds', { userId }),
        );
      } finally {
        if (inFlightProfileFetch.current === entry) {
          inFlightProfileFetch.current = null;
        }
      }
    })();
    inFlightProfileFetch.current = entry;
    return entry.promise;
  }, [supabase]);
  
  // Refresh user profile
  const refreshProfile = useCallback(async (): Promise<UserProfile | null> => {
    logger.debug('refreshProfile called', { userId: user?.id });
    if (!user) {
      logger.debug('refreshProfile: No user, returning null');
      return null;
    }

    try {
      const userProfile = await fetchProfile(user.id);
      logger.debug('refreshProfile: Fetched profile', { found: !!userProfile });

      if (userProfile) {
        setProfile(userProfile);
      } else {
        logger.debug('refreshProfile: No profile returned from fetchProfile');
      }
      return userProfile;
    } catch (error) {
      logger.error('refreshProfile: Error', error);
      return null;
    }
  }, [user, fetchProfile]);
  
  // Sign in with email - APPROACH 1: Email-First (No Phone Required)
  const signInWithEmail = useCallback(async (
    email: string,
    captchaToken?: string,
  ): Promise<{ success: boolean; error: string | null; needsPhone?: boolean; maskedPhone?: string; fullPhone?: string }> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[Auth][Approach1] Email-only signin for:', email);

      // APPROACH 1: Always send EMAIL OTP
      // No phone checking, no SMS during signup
      // Phone is added AFTER login via settings or admin
      const { error: emailError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/api/auth/callback`,
          shouldCreateUser: true,  // Create email-based user
          captchaToken,
        }
      });

      if (emailError) {
        console.error('[Auth][Approach1] Email OTP failed:', emailError.message);
        setError(emailError.message);
        return { success: false, error: emailError.message };
      }

      console.log('[Auth][Approach1] Email OTP sent successfully');
      return {
        success: true,
        error: null,
        needsPhone: false,  // Never need phone during signup
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign in with phone (SMS OTP) with auto-fallback to email
  const signInWithPhone = useCallback(async (
    phone: string,
    email?: string,
    captchaToken?: string,
  ): Promise<{ success: boolean; error: string | null; fallbackUsed?: boolean }> => {
    try {
      setIsLoading(true);
      setError(null);

      // Preserve current URL parameters
      const currentParams = new URLSearchParams(window.location.search);
      const invite = currentParams.get('invite');
      const next = currentParams.get('next');

      // Build callback URL with preserved parameters
      let callbackUrl = `${window.location.origin}/api/auth/callback`;
      const callbackParams = new URLSearchParams();

      if (invite) {
        callbackParams.set('invite', invite);
      }
      if (next) {
        callbackParams.set('next', next);
      }

      if (callbackParams.toString()) {
        callbackUrl += `?${callbackParams.toString()}`;
      }

      console.log('[Auth] Attempting SMS OTP to phone:', phone);

      // Log phone format before sending OTP (critical for debugging)
      logPhoneFormat('signInWithPhone - before signInWithOtp', phone, {
        email: email,
        hasEmail: !!email,
        callbackUrl: callbackUrl,
      });

      // Validate phone format before sending
      if (!isValidE164(phone)) {
        console.error('[Auth] Phone is not valid E.164 format:', phone);
        setError('Invalid phone format. Phone must be in E.164 format (e.g., +18676896780)');
        return {
          success: false,
          error: 'Invalid phone format. Please enter phone with country code (e.g., +1 for US/Canada)',
          fallbackUsed: false
        };
      }

      // Try SMS OTP first.
      // shouldCreateUser must stay FALSE on this phone path: per the email-first
      // architecture (CLAUDE.md), phone is a SECONDARY identifier added via
      // settings/admin after an email account exists. Allowing creation here
      // produces phone-only auth.users rows with no public.users profile —
      // orphans that block the real owner from ever adding that number, and
      // a free vector for bots to burn SMS credits. New users fall through to
      // the email branch below (shouldCreateUser: true), which is correct.
      const { error: smsError } = await supabase.auth.signInWithOtp({
        phone,
        options: {
          shouldCreateUser: false,
          captchaToken,
          data: email ? {
            email,
            notification_email: email
          } : undefined
        }
      });

      // If SMS succeeds, return success
      if (!smsError) {
        console.log('[Auth] SMS OTP sent successfully');
        return { success: true, error: null, fallbackUsed: false };
      }

      console.error('[Auth] SMS OTP failed:', smsError.message);

      // Auto-fallback to email OTP if SMS fails and email is provided
      if (email) {
        console.log('[Auth] Falling back to email OTP for:', email);

        const { error: emailError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: callbackUrl,
            shouldCreateUser: true,
            captchaToken,
            data: {
              phone,
              fallback_from_sms: true
            }
          }
        });

        if (emailError) {
          console.error('[Auth] Email fallback also failed:', emailError.message);
          setError(emailError.message);
          return {
            success: false,
            error: `SMS failed: ${smsError.message}. Email fallback also failed: ${emailError.message}`,
            fallbackUsed: true
          };
        }

        console.log('[Auth] Email OTP fallback sent successfully');
        return {
          success: true,
          error: null,
          fallbackUsed: true
        };
      }

      // No email provided for fallback
      setError(smsError.message);
      return {
        success: false,
        error: `SMS OTP failed: ${smsError.message}. No email provided for fallback.`,
        fallbackUsed: false
      };

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in';
      setError(errorMessage);
      return { success: false, error: errorMessage, fallbackUsed: false };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign in with OAuth provider (e.g., Microsoft/Azure)
  const signInWithOAuth = useCallback(async (provider: 'azure'): Promise<{ success: boolean; error: string | null }> => {
    try {
      setIsLoading(true);
      setError(null);

      const currentParams = new URLSearchParams(window.location.search);
      const invite = currentParams.get('invite');
      const next = currentParams.get('next');
      const redirect = currentParams.get('redirect');

      let callbackUrl = `${window.location.origin}/api/auth/callback`;
      const callbackParams = new URLSearchParams();

      if (invite) callbackParams.set('invite', invite);
      if (next) callbackParams.set('next', next);
      if (redirect) callbackParams.set('redirect', redirect);

      if (callbackParams.toString()) {
        callbackUrl += `?${callbackParams.toString()}`;
      }

      console.log('[Auth] Starting OAuth sign-in with provider:', provider, 'redirectTo:', callbackUrl);

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl,
          scopes: 'email',
        },
      });

      if (oauthError) {
        console.error('[Auth] OAuth sign-in error:', oauthError.message);
        setError(oauthError.message);
        return { success: false, error: oauthError.message };
      }

      return { success: true, error: null };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to sign in with OAuth';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  // Sign out
  const signOut = useCallback(async (): Promise<void> => {
    console.log('[Auth] SignOut called');
    try {
      setIsLoading(true);
      
      // Use the logout API route for better cookie handling
      console.log('[Auth] Calling logout API');
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Logout failed');
      }
      
      const data = await response.json();
      console.log('[Auth] Logout API response:', data);
      
      // Clear local state
      console.log('[Auth] Clearing local state');
      authEpochRef.current += 1;
      setUser(null);
      setSession(null);
      setProfile(null);
      setStatus('unauthenticated');
      
      // Clear any persisted auth data
      if (typeof window !== 'undefined') {
        const projectRef = process.env.NEXT_PUBLIC_SUPABASE_URL?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'lpjvndsvkvqgkdkvacnn';
        localStorage.removeItem(`sb-${projectRef}-auth-token`);
        localStorage.removeItem('supabase.auth.token');
      }
      
      // Redirect to login
      console.log('[Auth] Redirecting to /login');
      router.push('/login');
    } catch (error) {
      console.error('[Auth] Error signing out:', error);
      
      // Even if logout fails, clear local state and redirect
      authEpochRef.current += 1;
      setUser(null);
      setSession(null);
      setProfile(null);
      setStatus('unauthenticated');
      router.push('/login');
    } finally {
      setIsLoading(false);
    }
  }, [router]);
  
  // Refresh session
  const refreshSession = useCallback(async (options?: { silent?: boolean }): Promise<Session | null> => {
    try {
      logger.debug('Refreshing session', { silent: !!options?.silent });
      
      // Store the current profile before refreshing
      const currentProfile = profile;
      const currentUserId = user?.id;
      
      // Set loading state only if not a silent refresh
      if (!options?.silent) {
        setIsLoading(true);
      }
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error('Error refreshing session', error);
        if (!options?.silent) {
          setIsLoading(false);
        }
        return null;
      }

      if (data?.session) {
        logger.debug('Session refreshed successfully');
        
        // IMPORTANT: First update session and user
        // TypeScript safety check (data.session is guaranteed to be non-null here)
        const session = data.session!;
        setSession(session);
        setUser(session.user);
        setStatus('authenticated'); // Ensure we're properly authenticated after refresh
        
        // Preserve the profile during the refresh if user ID matches
        // This is critical to prevent the profile from disappearing in the header
        if (currentProfile && currentProfile.auth_id === data.session.user.id) {
          logger.debug('Preserving existing profile during refresh');
          // Explicitly set profile again to ensure it's still in state
          setProfile(currentProfile);
          
          // Add a small delay before fetching fresh profile to allow UI to stabilize
          setTimeout(async () => {
            try {
              // Ensure session and user are still valid before fetching profile
              if (!data.session || !data.session.user) {
                logger.warn('No valid session/user found during background profile refresh');
                return;
              }
              // Quietly refresh profile in the background without causing UI flicker
              logger.debug('Background refreshing profile data');
              const userProfile = await fetchProfile(data.session.user.id);
              if (userProfile) {
                // Only update if the profile has actually changed
                if (JSON.stringify(userProfile) !== JSON.stringify(currentProfile)) {
                  logger.debug('Profile updated with fresh data');
                  setProfile(userProfile);
                } else {
                  logger.debug('Profile unchanged, preserving current data');
                }
              }
            } catch (profileError) {
              logger.error('Error in background profile refresh', profileError);
              // Keep existing profile on error
            }
          }, 500);
        } else {
          // No existing profile or user ID changed - fetch a fresh profile
          logger.debug('Fetching fresh profile after session refresh');
          try {
            const userProfile = await fetchProfile(data.session.user.id);
            if (userProfile) {
              setProfile(userProfile);
            }
          } catch (profileError) {
            logger.error('Error fetching profile during session refresh', profileError);

            // If we had a profile before but failed to fetch a new one,
            // preserve the old one if we're still looking at the same user
            if (currentProfile && currentUserId === data.session.user.id) {
              logger.debug('Restoring previous profile after fetch failure');
              setProfile(currentProfile);
            }
          }
        }
        
        if (!options?.silent) {
          setIsLoading(false);
        }
        return data.session;
      }
      
      if (!options?.silent) {
        setIsLoading(false);
      }
      return null;
    } catch (error) {
      logger.error('Error in refreshSession', error);
      if (!options?.silent) {
        setIsLoading(false);
      }
      return null;
    }
  }, [supabase, profile, user, fetchProfile]);
  
  // Function to synchronize auth state from localStorage if needed
  const syncAuthFromLocalStorage = useCallback(() => {
    try {
      // Skip if already authenticated, initializing, or currently loading
      if (isAuthenticated || status === ('loading' as AuthStatus) || isLoading || !window.localStorage) {
        return false;
      }

      // Check for auth data in localStorage
      const persistedAuth = localStorage.getItem('supabase.auth.token');
      
      if (persistedAuth) {
        // Found persisted auth data, trigger refresh to validate it
        logger.debug('Found persisted auth data in localStorage, refreshing session');
        // We'll return true but defer the actual refresh to avoid race conditions
        setTimeout(() => {
          if (!isAuthenticated && status !== ('loading' as AuthStatus)) {
            refreshSession();
          }
        }, 100);
        return true;
      }
      
      return false;
    } catch (err) {
      logger.error('Error syncing from localStorage', err);
      return false;
    }
  }, [isAuthenticated, status, isLoading, refreshSession]);
  
  // Initialize auth - use a ref to prevent infinite loops
  const initRef = useRef(false);
  
  useEffect(() => {
    let mounted = true;
    let timeout: NodeJS.Timeout | null = null;

    const initializeAuth = async () => {
      setIsLoading(true);
      setStatus('loading');
      setError(null);

      // Small delay to ensure cookies are ready after redirect
      const urlParams = new URLSearchParams(window.location.search);
      const hasLoginSuccess = urlParams.has('login_success');

      if (hasLoginSuccess) {
        logger.debug('Detected login success, waiting for cookies to settle...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      try {
        // Try to restore session. getSession() can no longer hang forever (the
        // browser client's auth lock has a bounded acquire — see
        // lib/supabase-clients.ts), but we still race a defensive timeout so a
        // pathologically slow network can't strand init on 'loading' either.
        logger.debug('Checking for existing session...');
        const { data: { session }, error: sessionError } = await Promise.race([
          supabase.auth.getSession(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('getSession timed out during auth init')),
              INIT_GETSESSION_TIMEOUT_MS,
            ),
          ),
        ]);

        if (!mounted) return;

        if (sessionError) {
          logger.error('Error getting session', sessionError);
          setStatus('unauthenticated');
          setUser(null);
          setProfile(null);
          setSession(null);
          setIsLoading(false);
          setIsInitialized(true);
          setError(sessionError.message);
          return;
        }

        logger.debug('Session check result', { found: !!session, userId: session?.user?.id });

        if (session) {
          setSession(session);
          setUser(session.user);
          setStatus('authenticated');
          // Leave 'loading' IMMEDIATELY once the session is known. The profile
          // then loads progressively — first paint never blocks on the users
          // query (that await used to race the AuthLoader breaker 5s-vs-5s).
          setIsLoading(false);
          setIsInitialized(true);

          logger.debug('Fetching profile during init', { userId: session.user.id });
          const userProfile = await fetchProfile(session.user.id);
          logger.debug('Profile fetch result during init', { found: !!userProfile });
          if (mounted) {
            setProfile(userProfile);
          }

          // If we have login_success param and we're authenticated, redirect immediately
          if (hasLoginSuccess && window.location.pathname === '/login') {
            logger.debug('Login success with valid session, redirecting');
            const destination = userProfile ? '/dashboard' : '/onboarding';
            window.location.replace(destination);
          }
          return;
        }

        // No session — terminal unauthenticated state.
        logger.debug('No session found, setting unauthenticated state');
        setStatus('unauthenticated');
        setUser(null);
        setProfile(null);
        setSession(null);
        setIsLoading(false);
        setIsInitialized(true);
      } catch (err) {
        // getSession threw or timed out (e.g. corrupt cookie decode, pathological
        // network). NEVER leave the app stuck on 'loading' — that is the mobile
        // "Authentication Taking Too Long" dead-end. Try one more recovery read
        // under the SAME timeout race (the bounded auth lock caps lock waits, but
        // not a hung network, so this read is timed out too), then fail terminal.
        logger.error('initializeAuth failed', err);
        if (!mounted) return;

        let recovered: Session | null = null;
        try {
          const { data } = await Promise.race([
            supabase.auth.getSession(),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new Error('recovery getSession timed out during auth init')),
                INIT_GETSESSION_TIMEOUT_MS,
              ),
            ),
          ]);
          recovered = data.session ?? null;
        } catch (retryErr) {
          logger.error('Recovery getSession also failed', retryErr);
        }
        if (!mounted) return;

        if (recovered) {
          setSession(recovered);
          setUser(recovered.user);
          setStatus('authenticated');
          setIsLoading(false);
          setIsInitialized(true);
          const userProfile = await fetchProfile(recovered.user.id);
          if (mounted) setProfile(userProfile);
        } else {
          setStatus('unauthenticated');
          setUser(null);
          setProfile(null);
          setSession(null);
          setIsLoading(false);
          setIsInitialized(true);
          setError(err instanceof Error ? err.message : 'Authentication failed to initialize');
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      if (timeout) clearTimeout(timeout);
    };
  }, []);
  
  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug('Auth state changed', { event, userId: session?.user?.id });

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        logger.debug('Processing auth event', { event, hasProfile: !!profile });

        // Only update state if it's actually changing
        const isNewUser = user?.id !== session.user.id;

        setSession(session);
        setUser(session.user);
        setStatus('authenticated');
        setIsLoading(false);
        setIsInitialized(true);

        // DEADLOCK GUARD: auth-js v2.89 awaits this callback while HOLDING its
        // auth lock (the awaited _notifyAllSubscribers call sites — notably
        // _recoverAndRefresh's SIGNED_IN inside initialize()'s lock, and the
        // signIn/verifyOtp/refresh flows), and every Supabase query needs that
        // same lock to read its access token (getSession). Awaiting a query
        // here therefore wedges page-load queries behind the lock until an
        // external timeout breaks the cycle (~5s, verified on prod: server
        // answered in ~200ms once the callback returned). All Supabase work —
        // including the redirect that depends on it — is deferred so the
        // callback returns immediately and the lock frees.
        const epoch = authEpochRef.current;
        setTimeout(async () => {
          // A SIGNED_OUT may land between scheduling and running this block
          // (cross-tab broadcast) — never resurrect a cleared profile or
          // redirect a signed-out user.
          if (authEpochRef.current !== epoch) return;

          // Whether THIS flow knows a profile exists — drives the login
          // redirect deterministically (no dependency on ref/commit timing).
          let hasProfile: boolean;

          // Only fetch profile if we don't have one or if it's a different user
          if (!profile && !profileRef.current || isNewUser) {
            logger.debug('Fetching profile in onAuthStateChange', { userId: session.user.id });
            const userProfile = await fetchProfile(session.user.id);
            if (authEpochRef.current !== epoch) return;
            logger.debug('Profile result in onAuthStateChange', { found: !!userProfile });
            hasProfile = !!userProfile;
            if (userProfile) {
              setProfile(userProfile);
            } else {
              logger.debug('No profile found in onAuthStateChange, scheduling retry');
              // Retry profile fetch after a delay if it failed
              setTimeout(async () => {
                logger.debug('Retrying profile fetch after delay');
                const retryProfile = await fetchProfile(session.user.id);
                if (retryProfile && authEpochRef.current === epoch) {
                  logger.debug('Retry successful, setting profile');
                  setProfile(retryProfile);
                }
              }, 2000); // Retry after 2 seconds
            }
          } else {
            logger.debug('Profile already exists, preserving it');
            hasProfile = true;
            // Restore profile from ref if state was lost
            if (!profile && profileRef.current) {
              logger.debug('Restoring profile from ref');
              setProfile(profileRef.current);
            }
          }

          // If we're on the login page after sign in, redirect. Runs AFTER the
          // profile work above so the existing-user vs onboarding decision
          // never races the fetch.
          // NEVER redirect on an anonymous session: Pattern A signup mints one
          // mid-flow (signInAnonymously → updateUser({ email })), and navigating
          // here kills the in-flight updateUser — the email never attaches and
          // the signup is stranded as an orphaned anonymous user. Anonymous
          // sessions (Pattern A mid-signup or room guests) are not completed
          // logins; their flows own their redirects.
          // ALSO stand down when the URL carries a next=/redirect= param, OR
          // when the login form just verified an OTP here (it stamps
          // auth_login_success/auth_login_time synchronously before its own
          // 500ms redirect): the form owns those redirects. Historically this
          // context redirect always lost that race because the profile fetch
          // above deadlocked ~5s — now that the fetch is fast it must keep
          // losing it on purpose, or plain logins would change destination.
          let formJustVerified = false;
          try {
            const stamp = Number(localStorage.getItem('auth_login_time') || 0);
            formJustVerified =
              localStorage.getItem('auth_login_success') === 'true' &&
              Date.now() - stamp < 30_000;
          } catch {
            // Storage unavailable → fall through to the context redirect.
          }
          const loginSearch = new URLSearchParams(window.location.search);
          const formOwnsRedirect =
            loginSearch.has('next') || loginSearch.has('redirect') || formJustVerified;
          if (
            window.location.pathname === '/login' &&
            !session.user.is_anonymous &&
            !formOwnsRedirect
          ) {
            logger.debug('Signed in on login page, redirecting');
            // Use a small delay to ensure state is fully updated
            setTimeout(() => {
              if (authEpochRef.current !== epoch) return;
              const redirectTo = hasProfile ? '/dashboard/moments' : '/onboarding';
              window.location.replace(redirectTo);
            }, 100);
          }
        }, 0);
      } else if (event === 'INITIAL_SESSION' && !session) {
        // auth-js finished initializing and there is NO session. This is the
        // backstop for the case where initializeAuth's own path failed to reach a
        // terminal state — make sure we always leave 'loading' rather than hang on
        // the "Authentication Taking Too Long" screen. Never downgrade an already
        // authenticated state (a SIGNED_IN may have landed first).
        logger.debug('INITIAL_SESSION with no session — finalizing unauthenticated');
        setIsLoading(false);
        setIsInitialized(true);
        setStatus((prev) => (prev === 'authenticated' ? prev : 'unauthenticated'));
      } else if (event === 'SIGNED_OUT') {
        authEpochRef.current += 1;
        setSession(null);
        setUser(null);
        setProfile(null);
        setStatus('unauthenticated');
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        logger.debug('TOKEN_REFRESHED event, preserving profile');
        setSession(session);
        setUser(session.user);
        // IMPORTANT: Do not clear profile on token refresh
      } else if (event === 'USER_UPDATED' && session) {
        setSession(session);
        setUser(session.user);
        // Re-fetch profile on user update — deferred for the same deadlock
        // reason as above (USER_UPDATED is also emitted under the auth lock).
        const epoch = authEpochRef.current;
        setTimeout(async () => {
          const userProfile = await fetchProfile(session.user.id);
          if (userProfile && authEpochRef.current === epoch) {
            setProfile(userProfile);
          }
        }, 0);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, supabase.auth]);
  
  // Handle navigation events - sync from localStorage if we lose auth state
  useEffect(() => {
    // Try to sync auth state if we think we're unauthenticated but localStorage says otherwise
    if (status === 'unauthenticated' && !isLoading) {
      syncAuthFromLocalStorage();
    }
    
    // Listen for focus events (when returning to tab)
    const handleFocus = () => {
      // Skip auth refresh if we're in a LiveKit room to prevent disconnection
      const pathname = window.location.pathname;
      const isInRoom = pathname.includes('/rooms/') || pathname.includes('/custom');
      
      if (isInRoom) {
        logger.debug('Skipping auth refresh - active room session');
        return;
      }
      
      // Debounce: Skip if we refreshed less than 30 seconds ago
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTime.current;
      const MIN_REFRESH_INTERVAL = 30000; // 30 seconds
      
      if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
        logger.debug('Skipping refresh - too soon since last refresh', { secondsAgo: Math.round(timeSinceLastRefresh / 1000) });
        return;
      }

      if (isAuthenticated && user) {
        logger.debug('Window focused, verifying auth state');
        lastRefreshTime.current = now;
        
        // Use silent refresh for authenticated users to avoid UI flicker
        refreshSession({ silent: true }).then(session => {
          if (!session && user) {
            logger.debug('Session invalid after focus, refreshing');
            syncAuthFromLocalStorage();
          }
        });
      } else if (!isAuthenticated && status !== 'loading') {
        // Check if we should be authenticated
        syncAuthFromLocalStorage();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, isLoading, refreshSession, status, syncAuthFromLocalStorage, user]);
  
  // Add regular profile refresh for long-running sessions
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;
    
    if (isAuthenticated && user) {
      refreshInterval = setInterval(() => {
        // Skip scheduled refresh if we're in a LiveKit room
        const pathname = window.location.pathname;
        const isInRoom = pathname.includes('/rooms/') || pathname.includes('/custom');
        
        if (isInRoom) {
          logger.debug('Skipping scheduled profile refresh - active room session');
          return;
        }

        logger.debug('Running scheduled profile refresh');
        refreshProfile();
      }, 5 * 60 * 1000); // Every 5 minutes
    }
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [isAuthenticated, user, refreshProfile]);
  
  const contextValue: SupabaseAuthContextType = {
    isAuthenticated,
    user,
    profile,
    session,
    status,
    error,
    isLoading,
    signInWithEmail,
    signInWithPhone,
    signInWithOAuth,
    signOut,
    refreshSession,
    refreshProfile,
    // Backward compatibility properties
    authStatus: status,
    isUsingImprovedAuth: true
  };
  
  return (
    <SupabaseAuthContext.Provider value={contextValue}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

// Hook to use the auth context
export function useSupabaseAuth() {
  const context = useContext(SupabaseAuthContext);
  
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  
  return context;
}